import type { TFunction } from 'i18next'

import { MARKET_PROGRAM_COLOR_MAP, MARKET_PROGRAMS_COLOR_PALETTE } from '@/constants/marketPrograms'
import {
  BAR_SIDE_LEGEND_MIN_WIDTH,
  BAR_SIDE_LEGEND_TEXT_WIDTH,
  BAR_SIDE_LEGEND_WIDTH,
  PIE_SIDE_LEGEND_LEFT,
  PIE_SIDE_LEGEND_MIN_WIDTH,
  PIE_SIDE_LEGEND_SERIES_LAYOUT,
  PIE_SIDE_LEGEND_TEXT_WIDTH,
  SIDE_LEGEND_OPTIONS,
} from '@/features/site/constants/revenueChart'
import type { RevenueDataRow } from '@/features/site/types/revenue'
import { getMonthShortNames } from '@/utils/date'

import { convertToStandardMarketProgramType } from './convertToStandardMarketProgramType'
import { getMarketProgramLabel } from './getMarketProgramLabel'
import { isFeeMarketProgram } from './isFeeMarketProgram'

const numberFormat = new Intl.NumberFormat('nl', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

function formatAmount(currency: string, value: number) {
  return currency ? `${currency} ${numberFormat.format(value)}` : numberFormat.format(value)
}

type SeriesData = {
  name: string
  type: string
  stack: string
  label: {
    show: boolean
  }
  data: number[]
}

// Assign a fixed color to each market program based on its position in the canonical MARKET_PROGRAMS list
export function assignColorsToMarketPrograms(rawPrograms: string[], t: TFunction) {
  return rawPrograms.reduce(
    (acc, program, index) => {
      const standardType = convertToStandardMarketProgramType(program)
      const translatedName = getMarketProgramLabel(t, program)
      acc[translatedName] =
        MARKET_PROGRAM_COLOR_MAP[standardType] ??
        MARKET_PROGRAMS_COLOR_PALETTE[index % MARKET_PROGRAMS_COLOR_PALETTE.length]
      return acc
    },
    {} as Record<string, string>,
  )
}

/**
 * Removes the fee market programs from revenue data grouped by market program. Used for the
 * revenues bar chart, which only shows the programs that are considered revenues; the fee
 * programs are charted separately in the costs distribution chart.
 */
export function excludeFeeMarketPrograms(
  data: Record<string, Record<number, RevenueDataRow[]>>,
): Record<string, Record<number, RevenueDataRow[]>> {
  return Object.fromEntries(Object.entries(data).filter(([marketProgram]) => !isFeeMarketProgram(marketProgram)))
}

/**
 * Transforms revenue data grouped by market program and month into a structure
 * suitable for charting, using market program names as keys.
 *
 * @param data - The input data, where:
 *   - The first level keys are market program names.
 *   - The second level keys are month numbers (1-12).
 *   - The values are arrays of RevenueDataRow objects.
 * @returns - The transformed data, where:
 *   - programs - Market program names.
 *   - seriesData - The SeriesData[] to be used in the chart for every market.
 */
export function getChartDataUsingMapOfMarketProgramsGroupedByMonth(
  data: Record<string, Record<number, RevenueDataRow[]>>,
  t: TFunction,
) {
  const marketPrograms = Object.keys(data) // Get all market programs
  const groupedData: Record<string, Record<string, number>> = {}

  // Initialize all months with empty revenue data
  const monthNames = getMonthShortNames()

  for (const month of monthNames) {
    groupedData[month] = {}
  }

  for (const marketProgram of marketPrograms) {
    for (const [monthKey, rows] of Object.entries(data[marketProgram])) {
      const monthIndex = Number.parseInt(monthKey) - 1 // Convert month number to 0-based index
      const month = monthNames[monthIndex] // Get month name

      if (!groupedData[month][marketProgram]) {
        groupedData[month][marketProgram] = 0
      }

      for (const row of rows) {
        groupedData[month][marketProgram] += row.revenue
      }
    }
  }

  return marketPrograms.reduce(
    (acc, program) => {
      const marketProgramName = getMarketProgramLabel(t, program)

      return {
        programs: [...acc.programs, marketProgramName],
        seriesData: [
          ...acc.seriesData,
          {
            name: marketProgramName,
            type: 'bar',
            stack: 'total',
            label: { show: false },
            data: monthNames.map((month) => groupedData[month][program] ?? 0),
          },
        ],
      }
    },
    { programs: [], seriesData: [] } as { programs: string[]; seriesData: SeriesData[] },
  )
}

export function getRevenuesChartOptions(
  programs: string[],
  seriesData: SeriesData[],
  colors: Record<string, string>,
  currency: string,
) {
  return {
    color: programs.map((program) => colors[program]), // Apply consistent colors
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        let tooltipText = `<strong>${params[0].axisValue}</strong><br/>`
        for (const item of params) {
          tooltipText += `${item.marker} ${item.seriesName}: ${formatAmount(currency, item.value)}<br/>`
        }
        return tooltipText
      },
    },
    // The legend is listed beside the plot, in a column reserved by the grid, with long labels
    // truncated to that column so they stay clear of the bars
    legend: {
      ...SIDE_LEGEND_OPTIONS,
      right: 0,
      data: programs,
      textStyle: { width: BAR_SIDE_LEGEND_TEXT_WIDTH, overflow: 'truncate' },
    },
    grid: { left: 0, right: BAR_SIDE_LEGEND_WIDTH, top: 16, bottom: 0, containLabel: true },
    xAxis: { type: 'category', data: getMonthShortNames() },
    yAxis: { type: 'value', axisLabel: { formatter: (value: number) => formatAmount(currency, value) } },
    series: seriesData.map((series) => ({
      ...series,
      itemStyle: { color: colors[series.name] },
    })),
    // Too narrow to spare a legend column: move the legend below the plot as a scrollable row.
    // The default entry restores the side layout when the chart widens again, as ECharts does
    // not revert a media option on its own once its query stops matching.
    media: [
      {
        option: {
          legend: { orient: 'vertical', left: 'auto', right: 0, top: 'middle', bottom: 'auto' },
          grid: { right: BAR_SIDE_LEGEND_WIDTH, bottom: 0 },
        },
      },
      {
        query: { maxWidth: BAR_SIDE_LEGEND_MIN_WIDTH },
        option: {
          legend: { orient: 'horizontal', left: 'center', right: 'auto', top: 'auto', bottom: 0 },
          grid: { right: 16, bottom: 40 },
        },
      },
    ],
  }
}

// Row revenues are floats, so program totals accumulate floating point noise (e.g.
// 0.1 + 0.2 - 0.3 ≈ 5.55e-17). Totals are rounded to cents before any sign check to
// avoid phantom zero-value pie slices.
function roundToCents(value: number): number {
  return Number.parseFloat(value.toFixed(2))
}

/**
 * Selects the costs from per-program revenue totals: only fee programs whose total is
 * negative count as costs, returned as positive amounts. This is the single definition of
 * "costs" shared by the costs distribution chart and the costs widget, so they always show
 * the same numbers. A fee program netting positive (a credit) is not a cost; its amount
 * still contributes to the net revenue.
 */
export function getCostsByMarketProgram(programTotals: Record<string, number>): Record<string, number> {
  return Object.fromEntries(
    Object.entries(programTotals)
      .map(([marketProgram, total]) => [marketProgram, roundToCents(total)] as const)
      .filter(([marketProgram, total]) => isFeeMarketProgram(marketProgram) && total < 0)
      .map(([marketProgram, total]) => [marketProgram, Math.abs(total)]),
  )
}

function getProgramRevenueTotals(data: Record<string, Record<number, RevenueDataRow[]>>): Record<string, number> {
  const programTotals: Record<string, number> = {}

  // Iterate over market programs first
  for (const [marketProgram, months] of Object.entries(data)) {
    if (!programTotals[marketProgram]) {
      programTotals[marketProgram] = 0
    }

    for (const rows of Object.values(months)) {
      for (const row of rows) {
        programTotals[marketProgram] += row.revenue
      }
    }
  }

  return programTotals
}

type PieDatum = {
  name: string
  value: number
  itemStyle: { color: string }
}

function toPieDatum(rawMarketProgram: string, value: number, colors: Record<string, string>, t: TFunction): PieDatum {
  const marketProgramName = getMarketProgramLabel(t, rawMarketProgram)

  return {
    name: marketProgramName,
    value: Number.parseFloat(value.toFixed(2)),
    itemStyle: { color: colors[marketProgramName] }, // Apply consistent colors
  }
}

function getDistributionChartOptions(pieData: PieDatum[], currency: string) {
  return {
    tooltip: {
      trigger: 'item',
      formatter: ({ name, value, percent }: any) =>
        `${name}: ${formatAmount(currency, value)} (${numberFormat.format(percent)}%)`,
    },
    // Slice labels are drawn in the legend next to the donut instead of around it: outside
    // labels truncate and collide with each other as soon as there are many small slices.
    legend: {
      ...SIDE_LEGEND_OPTIONS,
      left: PIE_SIDE_LEGEND_LEFT,
      textStyle: { width: PIE_SIDE_LEGEND_TEXT_WIDTH, overflow: 'truncate' },
      formatter: (name: string) => {
        const datum = pieData.find((item) => item.name === name)
        return datum ? `${name}: ${formatAmount(currency, datum.value)}` : name
      },
    },
    series: [
      {
        type: 'pie',
        ...PIE_SIDE_LEGEND_SERIES_LAYOUT,
        data: pieData,
        label: { show: false },
        labelLine: { show: false },
      },
    ],
    // Too narrow for the legend beside the donut: stack it below as a single scrollable row. The
    // default entry restores the side layout when the chart widens again (see the bar chart).
    media: [
      {
        option: {
          legend: { orient: 'vertical', left: PIE_SIDE_LEGEND_LEFT, top: 'middle', bottom: 'auto' },
          series: [PIE_SIDE_LEGEND_SERIES_LAYOUT],
        },
      },
      {
        query: { maxWidth: PIE_SIDE_LEGEND_MIN_WIDTH },
        option: {
          legend: { orient: 'horizontal', left: 'center', top: 'auto', bottom: 0 },
          series: [{ center: ['50%', '45%'], radius: ['35%', '62%'] }],
        },
      },
    ],
  }
}

export function getRevenuesDistributionChartOptions(
  data: Record<string, Record<number, RevenueDataRow[]>>,
  colors: Record<string, string>,
  t: TFunction,
  currency: string,
) {
  // All market programs are considered revenues except the fee programs, which belong to the
  // costs distribution chart. A revenue program whose total is not positive is left out, as a
  // pie cannot represent it; it remains visible in the bar chart, widgets and tables.
  const pieData = Object.entries(getProgramRevenueTotals(data))
    .map(([name, value]) => [name, roundToCents(value)] as const)
    .filter(([name, value]) => !isFeeMarketProgram(name) && value > 0)
    .map(([name, value]) => toPieDatum(name, value, colors, t))

  return getDistributionChartOptions(pieData, currency)
}

export function getCostsDistributionChartOptions(
  data: Record<string, Record<number, RevenueDataRow[]>>,
  colors: Record<string, string>,
  t: TFunction,
  currency: string,
) {
  // Only the fee programs are considered costs, charted as absolute amounts — market
  // programs are never shown here, even when their total is negative (they stay in the
  // revenues bar chart, which supports negative bars). A fee program netting positive (a
  // credit) is left out of every chart: its amount still contributes to the net revenue
  // widget, the aggregated table rows and the CSV export, but is not itemized in any chart
  // (pending a product decision on how to present fee credits).
  const pieData = Object.entries(getCostsByMarketProgram(getProgramRevenueTotals(data))).map(([name, value]) =>
    toPieDatum(name, value, colors, t),
  )

  return getDistributionChartOptions(pieData, currency)
}
